import { useState } from 'react';
import { StatusChip } from './StatusChip';
import { timeAgo, sevVariant } from '../api/format';
import { api } from '../api/client';
import type { Alert } from '../api/types';

function parseTriggerContext(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

interface AlertDrawerProps {
  alert: Alert | null;
  onClose: () => void;
  onAction: () => void; // callback to refetch after mutation
}

export function AlertDrawer({ alert, onClose, onAction }: AlertDrawerProps) {
  const [acting, setActing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  if (!alert) return null;
  const ctx = parseTriggerContext(alert.trigger_context);

  const handleAction = async (action: string) => {
    if (action === 'mute' && !window.confirm(`Mute "${alert.title}"?`)) return;
    setActing(action); setActionError(null); setActionSuccess(null);
    try {
      await api.alertAction(alert.id, action);
      setActionSuccess(`Alert ${action}d successfully`);
      setTimeout(() => { onAction(); onClose(); }, 800);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed');
    } finally { setActing(null); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, cursor: 'pointer' }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 520, background: 'var(--bg-root)', borderLeft: '1px solid var(--border-subtle)', zIndex: 101, overflowY: 'auto', animation: 'fadeUp 0.2s ease' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-raised)', position: 'sticky', top: 0, zIndex: 1 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <StatusChip label={alert.severity} variant={sevVariant(alert.severity)} />
              <StatusChip label={alert.state} variant={alert.state === 'open' ? 'warning' : 'muted'} />
              {alert.owner && <span style={{ fontSize: 11, color: 'var(--text-ghost)' }}>→ {alert.owner}</span>}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginTop: 4 }}>{alert.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{alert.client_id} · {alert.type.replace(/_/g, ' ')}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-ghost)', fontSize: 20, cursor: 'pointer', padding: '4px 8px' }}>×</button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Detail */}
          <Section title="Detail">
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: 12, background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)' }}>
              {alert.detail}
            </div>
          </Section>

          {/* Timeline */}
          <Section title="Timeline">
            <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)' }}>
              <Row label="First fired" value={alert.first_fired_at} isTime />
              <Row label="Last fired" value={alert.last_fired_at} isTime />
              {alert.resolved_at && <Row label="Resolved at" value={alert.resolved_at} isTime />}
              {alert.muted_until && <Row label="Muted until" value={alert.muted_until} isTime />}
              <Row label="Age" value={timeAgo(alert.first_fired_at)} />
            </div>
          </Section>

          {/* Trigger Context */}
          <Section title="Trigger Context">
            {ctx && Object.keys(ctx).length > 0 ? (
              <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)' }}>
                {Object.entries(ctx).map(([k, v]) => (
                  <Row key={k} label={k} value={String(v)} />
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-ghost)', padding: 12, textAlign: 'center' }}>No trigger context</div>
            )}
          </Section>

          {/* Entity / Dedup */}
          <Section title="Identifiers">
            <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)' }}>
              <Row label="Alert ID" value={alert.id} mono />
              {alert.entity_id && <Row label="Entity ID" value={alert.entity_id} mono />}
              <Row label="Dedup key" value={alert.dedup_key} mono />
            </div>
          </Section>

          {/* Actions */}
          {alert.state === 'open' && (
            <Section title="Actions">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['acknowledge', 'resolve', 'mute'].map(action => (
                  <button key={action} onClick={() => handleAction(action)} disabled={acting !== null}
                    style={{
                      padding: '8px 16px', fontSize: 12, fontWeight: 600,
                      background: action === 'resolve' ? 'var(--green-bg)' : 'var(--bg-active)',
                      border: `1px solid ${action === 'resolve' ? 'var(--green-border)' : 'var(--border-subtle)'}`,
                      borderRadius: 'var(--radius-sm)',
                      color: action === 'resolve' ? 'var(--green)' : 'var(--text-secondary)',
                      cursor: acting ? 'wait' : 'pointer', opacity: acting && acting !== action ? 0.5 : 1,
                    }}>
                    {acting === action ? '...' : action.charAt(0).toUpperCase() + action.slice(1)}
                  </button>
                ))}
              </div>
              {actionError && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--red)' }}>⚠ {actionError}</div>}
              {actionSuccess && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--green)' }}>✓ {actionSuccess}</div>}
            </Section>
          )}
        </div>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-ghost)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value, mono, isTime }: { label: string; value: string; mono?: boolean; isTime?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border-faint)' }}>
      <span style={{ fontSize: 11, color: 'var(--text-ghost)' }}>{label}</span>
      <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: mono ? 'var(--font-mono)' : 'inherit', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>
        {value}
        {isTime && <span style={{ color: 'var(--text-dim)', marginLeft: 6 }}>({timeAgo(value)})</span>}
      </span>
    </div>
  );
}
